import type { AttractEntry, AttractStory } from '../types';
import type { CharacterProfile } from '../types';
import { CAMPAIGN_PLANNER_LIMITS, matchupForSlot, profileIdForSlot, type CampaignIdentity } from '../lib/campaignPlanner';

export const ATTRACT_TIMING = {
  characterImageDurationMs: 4000,
  characterDurationMs: 16000,
  titleDurationMs: 14000,
  storyDurationMs: 16000,
  longStoryDurationMs: 20000,
  versusDurationMs: 12000,
  characterSelectDurationMs: 16000,
  transitionDurationMs: 900,
  pressStartDelayMs: 4000,
  pressStartIntervalMs: 17000,
  pressStartVisibleMs: 3000,
} as const;

const shot = (
  id: string,
  image: string,
  caption: string,
  characterIds: string[],
  transition: AttractStory['shots'][number]['transition'],
  dialogue?: AttractStory['shots'][number]['dialogue'],
): AttractStory['shots'][number] => ({
  id,
  image,
  caption,
  characterIds,
  transition,
  dialogue,
  durationMs: 4000,
  cameraMotion: { type: id.endsWith('1') ? 'push-in' : 'parallax', intensity: 0.35 },
});

export const stories: AttractStory[] = [
  {
    id: 'cheater-01', title: 'The Untouchable Queue', durationMs: ATTRACT_TIMING.storyDurationMs,
    shots: [
      shot('queue-1', '/assets/attract/story/cheater/01-basement-queue.webp', 'YEAR ONE // A basement light never switches off.', ['the-cheater'], 'halftone-reveal'),
      shot('queue-2', '/assets/attract/story/cheater/02-first-fake-loadout.webp', 'Every impossible inventory begins with one counterfeit click.', ['the-cheater'], 'panel-slide', { speaker: 'MOM — UPSTAIRS', text: 'Clean up before dinner!' }),
      shot('queue-3', '/assets/attract/story/cheater/03-old-guard-fails.webp', 'The first sentinels arrive late, tired, and already cracked.', ['the-cheater'], 'impact-cut'),
      shot('queue-4', '/assets/attract/story/cheater/04-first-smacker-falls.webp', 'The first Smacker calls it luck. The replay calls it something else.', ['the-cheater', 'jax'], 'ink-wipe'),
    ],
  },
  {
    id: 'cheater-02', title: 'The Broken Watch', durationMs: ATTRACT_TIMING.storyDurationMs,
    shots: [
      shot('watch-1', '/assets/attract/story/cheater/05-vac-sentinel.webp', 'A new watchman enters the server. Its badge is bright; its knees are not.', ['the-cheater'], 'page-turn'),
      shot('watch-2', '/assets/attract/story/cheater/06-vacnet-swarm.webp', 'Networks learn. The Cheater learns what they learned first.', ['the-cheater'], 'halftone-reveal'),
      shot('watch-3', '/assets/attract/story/cheater/07-overwatch-defeat.webp', 'A thousand judges point at the screen. None can reach through it.', ['the-cheater'], 'panel-slide'),
      shot('watch-4', '/assets/attract/story/cheater/08-trust-factor-ruins.webp', 'Trust collapses into another pile beneath the desk.', ['the-cheater'], 'impact-cut'),
    ],
  },
  {
    id: 'cheater-03', title: 'Twenty Years, One Excuse', durationMs: ATTRACT_TIMING.storyDurationMs,
    shots: [
      shot('years-1', '/assets/attract/story/cheater/09-years-pass.webp', 'Monitors change. Hairlines move. The account survives.', ['the-cheater'], 'page-turn'),
      shot('years-2', '/assets/attract/story/cheater/10-roster-collage-a.webp', 'Absolution. Beastly. Wake. The names become match history.', ['the-cheater', 'absolution', 'beastly', 'wake'], 'panel-slide'),
      shot('years-3', '/assets/attract/story/cheater/11-roster-collage-b.webp', 'Rivals compare impossible losses and discover the same shadow.', ['the-cheater', 'phantom', 'vandal', 'prosper'], 'ink-wipe'),
      shot('years-4', '/assets/attract/story/cheater/12-still-cheating.webp', 'Older now. Better hardware. Same small room. Same smaller victory.', ['the-cheater'], 'impact-cut'),
    ],
  },
  {
    id: 'cheater-04', title: 'The Counterfeit Crown', durationMs: ATTRACT_TIMING.storyDurationMs,
    shots: [
      shot('crown-1', '/assets/attract/story/cheater/13-diamond-gloves.webp', 'Every glove is priceless when no receipt is real.', ['the-cheater'], 'halftone-reveal'),
      shot('crown-2', '/assets/attract/story/cheater/14-emerald-ruby-blades.webp', 'Emerald. Ruby. Sapphire. A museum rendered by a menu.', ['the-cheater'], 'panel-slide'),
      shot('crown-3', '/assets/attract/story/cheater/15-live-defense-fails.webp', 'The newest live defense arrives unfinished—and leaves in pieces.', ['the-cheater'], 'impact-cut'),
      shot('crown-4', '/assets/attract/story/cheater/16-signal-leaks.webp', 'The accumulated failures begin transmitting somewhere impossible.', ['the-cheater'], 'ink-wipe'),
    ],
  },
  {
    id: 'cheater-05', title: 'The Island Answers', durationMs: ATTRACT_TIMING.storyDurationMs,
    shots: [
      shot('island-1', '/assets/attract/story/cheater/17-island-signal.webp', 'The island tournament hears the signal beneath every dishonest win.', ['the-cheater'], 'page-turn'),
      shot('island-2', '/assets/attract/story/cheater/18-rivals-assemble.webp', 'Old rivals arrive separately. For once, they share the same target.', ['jax', 'vandal', 'prosper', 'savage'], 'panel-slide'),
      shot('island-3', '/assets/attract/story/cheater/19-hidden-controller.webp', 'Below the arena, the basement signal finds an abandoned laboratory.', ['the-cheater'], 'ink-wipe'),
      shot('island-4', '/assets/attract/story/cheater/20-switch-thrown.webp', 'One careless victory trips the machine that was waiting to exist.', ['the-cheater', 'kane'], 'impact-cut'),
    ],
  },
  {
    id: 'convergence-01', title: 'The Copy Furnace', durationMs: ATTRACT_TIMING.longStoryDurationMs,
    shots: [
      shot('furnace-1', '/assets/attract/story/convergence/01-lab-awakens.webp', 'Purple current floods the buried lab. Empty tables remember bodies.', ['the-cheater'], 'halftone-reveal'),
      shot('furnace-2', '/assets/attract/story/convergence/02-echoes-copied.webp', 'The machine steals no fighter—only perfect, furious echoes.', ['absolution', 'alaia', 'beastly'], 'panel-slide'),
      shot('furnace-3', '/assets/attract/story/convergence/03-torso-assembly.webp', 'Copies lock together in a torso taller than the arena.', ['the-convergence'], 'ink-wipe'),
      shot('furnace-4', '/assets/attract/story/convergence/04-limbs-assembly.webp', 'Every stance becomes a joint. Every rivalry becomes a tendon.', ['the-convergence'], 'impact-cut'),
      shot('furnace-5', '/assets/attract/story/convergence/05-heart-starts.webp', 'At its center, a counterfeit win begins to beat.', ['the-convergence', 'the-cheater'], 'page-turn'),
    ],
  },
  {
    id: 'convergence-02', title: 'Thirty-One Become One', durationMs: ATTRACT_TIMING.longStoryDurationMs,
    shots: [
      shot('giant-1', '/assets/attract/story/convergence/06-head-awakens.webp', 'A head not copied from anyone opens its own eyes.', ['the-convergence'], 'halftone-reveal'),
      shot('giant-2', '/assets/attract/story/convergence/07-hands-and-feet.webp', 'Hands like grandstands. Feet that make the island change shape.', ['the-convergence'], 'panel-slide'),
      shot('giant-3', '/assets/attract/story/convergence/08-smackers-unite.webp', 'The real Smackers stand together because the copies will not.', ['jax', 'pb', 'savage', 'skysong'], 'ink-wipe'),
      shot('giant-4', '/assets/attract/story/convergence/09-controller-revealed.webp', 'High above the lab, The Cheater discovers the monster answers him.', ['the-convergence', 'the-cheater'], 'impact-cut'),
      shot('giant-5', '/assets/attract/story/convergence/10-island-rise.webp', 'The Convergence rises. What happens next belongs to the game.', ['the-convergence'], 'page-turn'),
    ],
  },
];

export function getStory(id: string) {
  return stories.find((story) => story.id === id);
}

const versusStages = [
  'Violet Storm Rooftop',
  'The Copy Furnace',
  'Island Zero',
  'Abandoned Boardwalk Arcade',
  'Midnight Cargo Pier',
  'Smackers Headquarters',
] as const;

export function buildAttractCampaign(characters: CharacterProfile[], identity: CampaignIdentity): AttractEntry[] {
  const profileIds = characters.map((character) => character.id);
  const specials: Array<AttractEntry | 'versus'> = [
    { type: 'story', storyId: 'cheater-01', durationMs: ATTRACT_TIMING.storyDurationMs },
    'versus',
    { type: 'story', storyId: 'cheater-02', durationMs: ATTRACT_TIMING.storyDurationMs },
    { type: 'character-select', durationMs: ATTRACT_TIMING.characterSelectDurationMs },
    'versus',
    { type: 'story', storyId: 'cheater-03', durationMs: ATTRACT_TIMING.storyDurationMs },
    'versus',
    { type: 'character-select', durationMs: ATTRACT_TIMING.characterSelectDurationMs },
    { type: 'story', storyId: 'cheater-04', durationMs: ATTRACT_TIMING.storyDurationMs },
    { type: 'character-select', durationMs: ATTRACT_TIMING.characterSelectDurationMs },
    { type: 'story', storyId: 'cheater-05', durationMs: ATTRACT_TIMING.storyDurationMs },
    'versus',
    { type: 'story', storyId: 'convergence-01', durationMs: ATTRACT_TIMING.longStoryDurationMs },
    'versus',
    { type: 'story', storyId: 'convergence-02', durationMs: ATTRACT_TIMING.longStoryDurationMs },
    'versus',
    'versus',
    { type: 'finale', durationMs: Number.POSITIVE_INFINITY },
  ];

  const entries: AttractEntry[] = [{ type: 'title', durationMs: ATTRACT_TIMING.titleDurationMs }];
  let profileCursor = 0;
  let versusCursor = 0;
  for (const [specialIndex, special] of specials.entries()) {
    const profilesBeforeSpecial = Math.ceil(((specialIndex + 1) * profileIds.length) / specials.length)
      - Math.ceil((specialIndex * profileIds.length) / specials.length);
    for (let count = 0; count < profilesBeforeSpecial; count += 1) {
      entries.push({ type: 'character', characterId: profileIdForSlot(profileIds, identity, profileCursor), durationMs: ATTRACT_TIMING.characterDurationMs });
      profileCursor += 1;
    }
    if (special === 'versus') {
      const matchup = matchupForSlot(characters, identity, versusCursor);
      entries.push({
        type: 'versus',
        leftCharacterId: matchup.leftCharacterId,
        rightCharacterId: matchup.rightCharacterId,
        stageId: versusStages[(identity.campaignNumber * CAMPAIGN_PLANNER_LIMITS.versusSlotsPerCampaign + versusCursor) % versusStages.length],
        durationMs: ATTRACT_TIMING.versusDurationMs,
        easterEgg: matchup.cameoCharacterId ? { cameoCharacterId: 'vandal', matchupKey: matchup.matchupKey } : undefined,
      });
      versusCursor += 1;
    } else {
      entries.push(special);
    }
  }
  if (profileCursor !== profileIds.length) throw new Error('Attract campaign did not schedule every character profile exactly once.');
  return entries;
}
